import { useCallback, useMemo, useState } from "react";
import { Card, Flex, Grid, Link, QueryLoader, Text } from "@jsq/ds/core";
import {
  useGetPositionCaseDetailsQuery,
  useGetPositionsQuery,
} from "~/graphql/supergraphHooks";
import { toDbId } from "~/utils/graphql-helpers";
import { useCurrentCaseContext } from "../../context/CurrentCaseContext";
import { useIOReusableProfiles } from "../../flags";
import RiskRatingChangeDetailsModal from "../../riskRating/RiskRatingChangeDetailsModal";
import { positionColumnDefinition } from "./positionColumnDefinition";
import { header } from "./styles_vanilla.css";
import { PositionRowItem } from "./types";
import { buildViewAllPositionsHref, rowsFromQuery } from "./utils";

export type PositionsGridContext = {
  onInspectRiskRating: (position: PositionRowItem | null) => void;
};

const Positions = () => {
  const { caseId } = useCurrentCaseContext();
  const ioReusableProfilesEnabled = useIOReusableProfiles();

  const [selectedPosition, setSelectedPosition] = useState<PositionRowItem | null>(null);

  const positionsResult = useGetPositionsQuery({
    variables: { caseId },
    fetchPolicy: "no-cache",
  });

  const caseIds = useMemo(() => {
    return (positionsResult.data?.casePositionDetails ?? []).map((p) => String(p.caseId));
  }, [positionsResult.data]);

  const positionCaseDetailsResult = useGetPositionCaseDetailsQuery({
    variables: {
      filters: [{ field: "id", filterValues: caseIds }],
    },
    skip: caseIds.length === 0,
    fetchPolicy: "no-cache",
  });

  const positionsData = useMemo(() => {
    if (!positionsResult.data) return [];

    const rows = rowsFromQuery(positionsResult.data);

    const caseDetailsMap = new Map<
      string,
      { arenaId: number | null; entityId: number | null; positionId: number | null }
    >();
    for (const c of positionCaseDetailsResult.data?.complianceCasesSlice.rows ?? []) {
      caseDetailsMap.set(String(c.id), {
        arenaId: c.externalArena?.objectId ? parseInt(c.externalArena.objectId, 10) : null,
        entityId: c.externalEntity?.objectId ? parseInt(c.externalEntity.objectId, 10) : null,
        positionId: c.position?.id ? toDbId(c.position.id) : null,
      });
    }

    return rows.map((row) => {
      const details = caseDetailsMap.get(row.id);
      if (!details) return row;
      return { ...row, ...details };
    });
  }, [positionsResult.data, positionCaseDetailsResult.data]);

  const handleInspectRiskRating = useCallback((position: PositionRowItem | null) => {
    setSelectedPosition(position);
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPosition(null);
  }, []);

  const gridContext: PositionsGridContext = useMemo(
    () => ({
      onInspectRiskRating: handleInspectRiskRating,
    }),
    [handleInspectRiskRating],
  );

  const showViewAllLink = ioReusableProfilesEnabled && positionsData.length > 0;
  const viewAllPositionsHref = useMemo(
    () => buildViewAllPositionsHref(positionsData),
    [positionsData],
  );

  return (
    <Card>
      <Flex justifyContent="space-between" alignItems="center" className={header}>
        <Text variant="secondary-md">POSITIONS</Text>
        {showViewAllLink && (
          <Link
            href={viewAllPositionsHref}
            shouldOpenInNewTab
            icon="external-link"
            iconPosition="right"
            data-testid="view-all-positions-link"
          >
            View all related positions
          </Link>
        )}
      </Flex>
      <QueryLoader result={positionsResult}>
        <Grid<PositionRowItem>
          columnDefs={positionColumnDefinition}
          rowData={positionsData}
          context={gridContext}
          gridOptions={{
            rowHeight: 46,
            headerHeight: 48,
            autoSizeStrategy: {
              type: "fitGridWidth",
            },
            domLayout: "autoHeight",
          }}
          reactiveCustomComponents
        />
      </QueryLoader>
      <RiskRatingChangeDetailsModal
        positionData={selectedPosition?.position}
        riskRatingInfo={selectedPosition?.riskRatingInfo}
        onClose={handleCloseModal}
      />
    </Card>
  );
};

export default Positions;
